/**
 * Componente de navegación vertical (sidebar) para la versión desktop
 * Replicación de NavbarBottom pero con布局 vertical
 * Muestra los 4 botones principales: Chat, Radar, Alertas, Perfil
 * Incluye contadores de interacciones en tiempo real
 */

import React, { memo, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useInteractionCounter } from '../../hooks/useInteractionCounter';
import { useResetCountersMutation } from '../../hooks/useInteractions';
import { useInteractionCounterRealtime } from '../../hooks/useInteractionCounterRealtime';
import { BsBagHeart } from "react-icons/bs";
// Importar componentes de botones individuales
import ChatButton from './NavbarBottom/ChatButton';
import RadarButton from './NavbarBottom/RadarButton';
import AlertsButton from './NavbarBottom/AlertsButton';
import ProfileButton from './NavbarBottom/ProfileButton';
import Navbar from './Navbar';

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

const NavbarVertical = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  
  // Memoizar session para evitar re-renders cuando el objeto session cambia pero los datos relevantes son los mismos
  // Solo re-ejecutar si realmente cambia el user.id
  const stableSession = useMemo(() => session, [session?.user?.id]);
  
  // Memoizar userId para usarlo en mutaciones
  const userId = useMemo(() => session?.user?.id, [session?.user?.id]);
  
  // Ref para trackear valores anteriores y detectar cambios
  const previousValuesRef = useRef({
    interactionData: null,
    isResetting: false,
    userId: null,
  });
  
  // Usar hook personalizado para obtener datos del contador de interacciones
  const { data: interactionData = { visitas: 0, me_gustas: 0, mensajes: 0 }, isLoading: loading } = useInteractionCounter(stableSession);
  
  // Hook para reiniciar contadores de interacciones
  const { mutate: resetCounters, isPending: isResetting } = useResetCountersMutation(userId);
  
  // Estado optimista: si se está reseteando, mostrar como si ya estuviera en 0
  // Memoizado para evitar re-renders cuando isResetting cambia pero interactionData es el mismo
  const optimisticInteractionData = useMemo(() => {
    return isResetting ? 
      { ...interactionData, visitas: 0, me_gustas: 0 } : 
      interactionData;
  }, [interactionData, isResetting]);
  
  // Detectar qué cambió
  const currentValues = {
    interactionData,
    isResetting,
    userId,
  };
  
  const changedKeys = {};
  for (const key in currentValues) {
    if (JSON.stringify(previousValuesRef.current[key]) !== JSON.stringify(currentValues[key])) {
      changedKeys[key] = {
        old: previousValuesRef.current[key],
        new: currentValues[key],
      };
    }
  }
  
  if (Object.keys(changedKeys).length > 0 && DEBUG) {
    console.log('[NavbarVertical] Valores que cambiaron:', Object.keys(changedKeys));
    console.log('[NavbarVertical] Cambios:', changedKeys);
  }
  
  previousValuesRef.current = currentValues;
  
  /**
   * Callbacks para manejar cambios en tiempo real en los contadores
   * Memoizados para evitar re-creación en cada render
   * Estos callbacks NO actualizan estado local para evitar re-renders
   * La actualización de datos se maneja automáticamente via React Query invalidation
   */
  const handleVisitasChange = useCallback((nuevoValor, valorAnterior) => {
    // No-op: los datos se actualizan vía React Query
  }, []);
  
  const handleMeGustasChange = useCallback((nuevoValor, valorAnterior) => {
    // No-op: los datos se actualizan vía React Query
  }, []);
  
  const handleMensajesChange = useCallback((nuevoValor, valorAnterior) => {
    // No-op: los datos se actualizan vía React Query
  }, []);
  
  const handleCounterChange = useCallback((cambios, newData, oldData) => {
    // No-op: los datos se actualizan vía React Query
  }, []);
  
  /**
   * Maneja el reinicio de contadores cuando se navega a alertas
   * Memoizado para evitar re-creación en cada render
   */
  const handleResetCounters = useCallback(() => {
    if (optimisticInteractionData.visitas > 0 || optimisticInteractionData.me_gustas > 0) {
      if (DEBUG) console.log('[NavbarVertical] Reiniciando contadores');
      resetCounters();
    }
  }, [optimisticInteractionData.visitas, optimisticInteractionData.me_gustas, resetCounters]);
  
  /**
   * Usar hook específico para suscripciones en tiempo real a interaction_counter
   * Escucha cambios en las columnas del contador de interacciones del usuario actual
   */
  useInteractionCounterRealtime({
    onVisitasChange: handleVisitasChange,
    onMeGustasChange: handleMeGustasChange,
    onMensajesChange: handleMensajesChange,
    onCounterChange: handleCounterChange
  });
  
  // No renderizar nada si está cargando
  if (loading) {
    if (DEBUG) console.log('[NavbarVertical] No renderizando (loading)');
    return null;
  }
  
  if (DEBUG) console.log('[NavbarVertical] Renderizando componente con interactionData:', interactionData);
  
  return (
    <aside 
      className="fixed left-0 top-0 flex flex-col items-center justify-between h-full w-64 bg-neutral-900 border-r border-neutral-800 flex flex-col z-40"
    >
      <div className="w-full">
        <Navbar />
      </div>
      {/* Contenedor principal con disposición vertical */}
      <div 
        className="flex flex-col w-full"
      >
        {/* CHAT - muestra contador de mensajes */}
        <div
          onClick={() => navigate('/chats')}
          className="p-2 flex items-center gap-4 transition-colors text-left cursor-pointer hover:bg-neutral-800 rounded"
        >
          <ChatButton
            messageCount={optimisticInteractionData.mensajes}
            disableNavigation={true}
          />
          <span className={`text-gray-300 ${location.pathname === '/chats' || location.pathname.startsWith('/chats/') ? 'font-bold' : ''}`}>Mensajes</span>
        </div>
        
        {/* RADAR - no muestra contador */}
        <div
          onClick={() => navigate('/radar')}
          className="p-2 flex items-center gap-4 transition-colors text-left cursor-pointer hover:bg-neutral-800 rounded"
        >
          <RadarButton disableNavigation={true} />
          <span className={`text-gray-300 ${location.pathname === '/radar' ? 'font-bold' : ''}`}>Radar</span>
        </div>
        
        {/* ALERTAS - muestra contador de interacciones (visitas + me_gustas) */}
        <div
          onClick={() => {
            if (optimisticInteractionData.visitas > 0 || optimisticInteractionData.me_gustas > 0) {
              handleResetCounters();
            }
            navigate('/interacciones');
          }}
          className="p-2 flex items-center gap-4 transition-colors text-left cursor-pointer hover:bg-neutral-800 rounded"
        >
          <AlertsButton
            interactionCount={optimisticInteractionData.visitas + optimisticInteractionData.me_gustas}
            onReset={handleResetCounters}
            disableNavigation={true}
          />
          <span className={`text-gray-300 ${location.pathname === '/interacciones' ? 'font-bold' : ''}`}>Interacciones</span>
        </div>
        
        {/* PERFIL - no muestra contador */}
        <div
          onClick={() => navigate('/mi-cuenta')}
          className="p-2 flex items-center gap-4 transition-colors text-left cursor-pointer hover:bg-neutral-800 rounded"
        >
          <ProfileButton disableNavigation={true} />
          <span className={`text-gray-300 ${location.pathname === '/mi-cuenta' || location.pathname.startsWith('/mi-cuenta/') ? 'font-bold' : ''}`}>Perfil</span>
        </div>


      </div>

      <div className="h-16 w-full border-t border-neutral-800">
      {/* PERFIL - no muestra contador */}
        <button
          onClick={() => navigate('/tienda')}
          className="w-full h-16 p-2 flex items-center justify-center gap-4 transition-colors text-left"
        >
          <BsBagHeart className="w-6 h-6 text-gray-300" />
          <span className={`text-gray-300 ${location.pathname === '/tienda' ? 'font-bold' : ''}`}>Tienda</span>
        </button>

      </div>
    </aside>
  );
};

// Memoizar el componente para evitar re-renders innecesarios
// Solo se re-renderiza si interactionData cambia realmente
export default memo(NavbarVertical, (prevProps, nextProps) => {
  // NavbarVertical no recibe props, así que siempre debería ser el mismo
  // El memo aquí es para prevenir re-renders desde el padre
  return true;
});
